import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      setUser(null);
      setLoading(false);
      return;
    }
    try {
      const { data } = await api.get('/auth/me');
      setUser(data.user);
    } catch {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { checkAuth(); }, [checkAuth]);

  // Re-check auth on page visibility change (back button, tab switch)
  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        const token = localStorage.getItem('token');
        if (!token && user) {
          setUser(null);
        } else if (token) {
          checkAuth();
        }
      }
    };
    const handlePageShow = (e) => {
      // bfcache: browser restored page from cache
      if (e.persisted) {
        const token = localStorage.getItem('token');
        if (!token) {
          setUser(null);
        } else {
          checkAuth();
        }
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    window.addEventListener('pageshow', handlePageShow);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibility);
      window.removeEventListener('pageshow', handlePageShow);
    };
  }, [user, checkAuth]);

  const setAuthFromResponse = (data) => {
    localStorage.setItem('user', JSON.stringify(data.user));
    setUser(data.user);
  };

  const login = async (email, password) => {
    const { data } = await api.post('/auth/login', { email, password });
    // If 2FA required, don't set auth yet
    if (data.requires_2fa) return data;
    localStorage.setItem('token', data.token);
    localStorage.setItem('user', JSON.stringify(data.user));
    setUser(data.user);
    return data;
  };

  // Passwordless patient login: exchange a server-verified OTP token + phone
  // for a session. Creates the patient account on first use (no email needed).
  const loginWithOtp = async ({ phone, otp_token, name = null, connect_to = null, signup_source = null }) => {
    const payload = { phone, otp_token };
    if (name) payload.name = name;
    if (connect_to) payload.connect_to = connect_to;
    if (signup_source) payload.signup_source = signup_source;
    const { data } = await api.post('/auth/patient-otp-login', payload);
    localStorage.setItem('token', data.token);
    localStorage.setItem('user', JSON.stringify(data.user));
    setUser(data.user);
    return data;
  };

  const register = async (name, email, password, phone, role = 'patient', consent = false, otp_token = null, connect_to = null, signup_source = null, doctorExtras = null) => {
    const payload = { name, email, password, phone, role, consent };
    if (otp_token) payload.otp_token = otp_token;
    if (connect_to) payload.connect_to = connect_to;
    if (signup_source) payload.signup_source = signup_source;
    if (doctorExtras && role === 'doctor') Object.assign(payload, doctorExtras);
    const { data } = await api.post('/auth/register', payload);
    localStorage.setItem('token', data.token);
    localStorage.setItem('user', JSON.stringify(data.user));
    setUser(data.user);
    return data;
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
    // Clear any cached API state
    delete api.defaults.headers.common['Authorization'];
    // Tell the service worker to nuke its API cache so the next user on a
    // shared device never sees the previous user's cached dashboard.
    try {
      navigator.serviceWorker?.controller?.postMessage({ type: 'CLEAR_AUTH_CACHE' });
    } catch (_) { /* ignore */ }
    // Replace current history entry so back button can't return to protected page
    window.history.replaceState(null, '', '/login');
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, loginWithOtp, register, logout, setAuthFromResponse }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
